import { useRef, type InputHTMLAttributes } from 'react';
import clsx from 'clsx';
import type { SvgIconComponent } from '@mui/icons-material';
import type { FieldValues, Path, RegisterOptions, UseFormRegister } from 'react-hook-form';

interface TextInputProps<T extends FieldValues> extends InputHTMLAttributes<HTMLInputElement> {
  register: UseFormRegister<T>;
  registerName: Path<T>;
  setValue?: (value: string) => void;
  label?: string;
  registerProps?: RegisterOptions<T, Path<T>>;
  isSecondary?: boolean;
  isDisabled?: boolean;
  error?: FieldValues['errors'];
  placeholder?: string;
  icon?: SvgIconComponent;
  whisper?: string[];
  whisperLabel?: string;
  inputClassName?: string;
  iconClassName?: string;
  absoluteLabel?: boolean;
}

export const TextInput = <T extends FieldValues>({
  label,
  register,
  registerName,
  registerProps,
  isSecondary,
  isDisabled,
  error,
  placeholder,
  icon: Icon,
  className,
  iconClassName,
  inputClassName,
  whisper,
  whisperLabel,
  setValue,
  absoluteLabel,
  ...rest
}: TextInputProps<T>) => {
  const { ref, onBlur, ...others } = register(registerName, registerProps);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const variantClass = isSecondary ? 'bg-primary border-1 border-secondary-light' : 'bg-transparent border-1 border-primary';

  const handleWhisperSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const input = inputRef.current;
    if (input) {
      if (setValue) {
        setValue(input.value + '$' + event.target.value);
      }
    }
  };

  return (
    <div>
      <div className={`flex flex-col ${className}`}>
        <div className="relative flex justify-between">
          {label && (
            <label htmlFor={registerName} className={`text-sm mb-1 ${absoluteLabel && 'absolute -top-5'}`}>
              {label}
            </label>
          )}
          {whisper && (
            <select className="text-sm bg-transparent focus:outline-none" value="" onChange={handleWhisperSelect}>
              <option disabled value="">
                {whisperLabel}
              </option>
              {whisper.map((option, index) => (
                <option key={index} value={option}>
                  {option}
                </option>
              ))}
            </select>
          )}
        </div>
        <div
          className={`flex text-base items-center pl-2 py-1 rounded-md focus-within::outline-none focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent 
				${error ? 'border-red-500' : ''}
				${isDisabled ? 'bg-gray-200' : variantClass}
				`}>
          {Icon && <Icon className={iconClassName} />}
          <input
            type="text"
            id={registerName}
            className={clsx('w-full bg-transparent focus:outline-none p-2 h-full', inputClassName)}
            disabled={isDisabled}
            placeholder={placeholder}
            ref={(e) => {
              ref(e);
              inputRef.current = e;
            }}
            {...rest}
            {...others}
          />
        </div>
      </div>
      {error && <span className="text-xs text-red-500">{error.message}</span>}
    </div>
  );
};
